import { Injectable, Injector, computed, effect, inject, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from '../authentication/auth.service';
import { LogHelper } from '../logging/log-helper';
import { PermissionFilter } from '../model';

export type NotificationSection = 'application' | 'profile' | 'security';
export interface ProductNotification {
  id: string;
  title: string;
  message: string;
  createdAt: Date;
  expiresAt?: Date;
  read: boolean;
  /** `general` — appears in the notification bell panel.
   *  `account` — appears as a badge on the Account menu; specify `section` to badge a specific menu item. */
  type: 'general' | 'account';
  section?: NotificationSection;
  link?: string;
  action?: () => void;
  /** Persistent notifications cannot be dismissed and always count as unread. */
  persistent?: boolean;
}

export interface NotificationRule {
  /** Stable ID — must be unique across all rules. Used to deduplicate. */
  id: string;
  /** Reactive predicate. Any signals read here are tracked — rule re-evaluates when they change. */
  condition: () => boolean;
  /** Optional permission filter — same shape as NavigationElement.permission. Rule is suppressed when not satisfied. */
  permission?: PermissionFilter;
  notification: Omit<ProductNotification, 'id' | 'read' | 'persistent'>;
}

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly authService = inject(AuthService);
  private readonly injector = inject(Injector);
  private readonly _notifications = signal<ProductNotification[]>([]);
  private readonly registeredIds = new Set<string>();
  private readonly currentUser = toSignal(this.authService.user.data, { initialValue: null });

  readonly notifications = this._notifications.asReadonly();
  readonly generalNotifications = computed(() => this._notifications().filter((n) => n.type === 'general'));
  readonly unreadCount = computed(() => this.generalNotifications().filter((n) => n.persistent || !n.read).length);

  readonly accountSections = computed(() => {
    const acct = this._notifications().filter((n) => n.type === 'account');
    return {
      any: acct.length > 0,
      application: acct.some((n) => n.section === 'application'),
      profile: acct.some((n) => n.section === 'profile'),
      security: acct.some((n) => n.section === 'security'),
    };
  });

  async load(): Promise<void> {
    // Reserved for future server-driven notification loading.
  }

  registerRule(rule: NotificationRule): void {
    if (this.registeredIds.has(rule.id)) return;
    this.registeredIds.add(rule.id);

    effect(
      () => {
        const user = this.currentUser();
        let active = false;
        try {
          active = rule.condition() && (!rule.permission || this.authService.isSatisfied(rule.permission, user));
        } catch (err) {
          LogHelper.error('NotificationService', `rule condition threw for id="${rule.id}"`, err);
          return;
        }
        untracked(() => {
          const exists = this._notifications().some((n) => n.id === rule.id);
          if (active && !exists) {
            this._notifications.update((list) => [
              { ...rule.notification, id: rule.id, read: false, persistent: true },
              ...list,
            ]);
          } else if (!active && exists) {
            this._notifications.update((list) => list.filter((n) => n.id !== rule.id));
          }
        });
      },
      { injector: this.injector },
    );
  }

  markRead(id: string): void {
    this._notifications.update((list) => list.map((n) => (n.id === id && !n.persistent ? { ...n, read: true } : n)));
  }

  markAllRead(): void {
    this._notifications.update((list) =>
      list.map((n) => (n.persistent || n.type === 'account' ? n : { ...n, read: true })),
    );
  }

  dismiss(id: string): void {
    this._notifications.update((list) => list.filter((n) => n.persistent || n.id !== id));
  }

  /** Dev/testing helper — add a notification at runtime. */
  add(notification: Omit<ProductNotification, 'id' | 'read'> & { id?: string }): void {
    const id = notification.id ?? crypto.randomUUID();
    this._notifications.update((list) => [{ ...notification, id, read: false }, ...list]);
  }
}
